import { useMemo, useState } from "react";
import {
  useGetAvailableTeamsQuery,
  useGetPopularPlayersQuery,
  useSearchPlayersQuery,
} from "../store/api/mlbApi";
import {
  useGetNbaTopPlayersQuery,
  useSearchNbaPlayersQuery,
} from "../store/api/nbaApi";
import {
  useGetNflTopPlayersQuery,
  useSearchNflPlayersQuery,
} from "../store/api/nflApi";
import {
  filterTeamNames,
  getPlayerSearchCatalog,
  mergeDirectoryOptions,
  pickBySport,
  railTeamsFromNames,
  toFeaturedPlayerTiles,
  MIN_DIRECTORY_QUERY_LENGTH,
} from "../components/playerSearch/playerSearchCatalog";
import {
  filterNbaFranchises,
  getActiveNbaFranchises,
  railTeamsFromNbaFranchises,
} from "../utils/nbaFranchises";
import {
  filterNflFranchises,
  getActiveNflFranchises,
  railTeamsFromNflFranchises,
} from "../utils/nflFranchises";

export const usePlayerSearchPage = (sport) => {
  const catalog = getPlayerSearchCatalog(sport);
  const [searchTerm, setSearchTerm] = useState("");
  const skipPlayers = searchTerm.length < MIN_DIRECTORY_QUERY_LENGTH;

  const mlbSearch = useSearchPlayersQuery(
    skipPlayers ? null : searchTerm,
    { skip: sport !== "mlb" || skipPlayers }
  );
  const nbaSearch = useSearchNbaPlayersQuery(
    skipPlayers ? null : searchTerm,
    { skip: sport !== "nba" || skipPlayers }
  );
  const nflSearch = useSearchNflPlayersQuery(
    skipPlayers ? null : searchTerm,
    { skip: sport !== "nfl" || skipPlayers }
  );
  const teamsQuery = useGetAvailableTeamsQuery(undefined, {
    skip: sport !== "mlb" || !catalog.teamsEnabled,
  });
  const mlbFeatured = useGetPopularPlayersQuery("homeruns", {
    skip: sport !== "mlb" || !catalog.featured,
  });
  const nbaFeatured = useGetNbaTopPlayersQuery("points", {
    skip: sport !== "nba" || !catalog.featured,
  });
  const nflFeatured = useGetNflTopPlayersQuery({ category: "passing-yards" }, {
    skip: sport !== "nfl" || !catalog.featured,
  });

  const idleQuery = { data: undefined, isLoading: false, error: undefined, isError: false };
  const playerQuery = pickBySport(
    sport,
    { mlb: mlbSearch, nba: nbaSearch, nfl: nflSearch },
    idleQuery
  );
  const featuredQuery = pickBySport(
    sport,
    { mlb: mlbFeatured, nba: nbaFeatured, nfl: nflFeatured },
    idleQuery
  );
  const mlbTeamNames = teamsQuery.data?.teams || [];
  const nbaFranchises = getActiveNbaFranchises();
  const nflFranchises = getActiveNflFranchises();

  const searchQuery = useMemo(() => {
    const matchedTeams = pickBySport(
      sport,
      {
        mlb: catalog.teamsEnabled ? filterTeamNames(mlbTeamNames, searchTerm) : [],
        nba: filterNbaFranchises(nbaFranchises, searchTerm),
        nfl: filterNflFranchises(nflFranchises, searchTerm),
      },
      []
    );
    return {
      data: mergeDirectoryOptions({
        sport,
        players: playerQuery.data || [],
        teamNames: matchedTeams,
      }),
      isLoading: playerQuery.isLoading,
      error: playerQuery.error,
    };
  }, [
    catalog.teamsEnabled,
    mlbTeamNames,
    nbaFranchises,
    nflFranchises,
    playerQuery.data,
    playerQuery.error,
    playerQuery.isLoading,
    searchTerm,
    sport,
  ]);

  const featuredRows = pickBySport(
    sport,
    {
      mlb: mlbFeatured.data,
      nba: nbaFeatured.data?.players,
      nfl: nflFeatured.data?.players,
    },
    []
  );

  return {
    catalog,
    searchQuery,
    handleSearchInputChange: setSearchTerm,
    featuredTiles: toFeaturedPlayerTiles(sport, featuredRows || []),
    featuredLoading: Boolean(catalog.featured && featuredQuery.isLoading),
    featuredError: Boolean(catalog.featured && featuredQuery.isError),
    railTeams: pickBySport(
      sport,
      {
        mlb: railTeamsFromNames(mlbTeamNames),
        nba: railTeamsFromNbaFranchises(nbaFranchises),
        nfl: railTeamsFromNflFranchises(nflFranchises),
      },
      []
    ),
    railLoading: Boolean(sport === "mlb" && catalog.teamsEnabled && teamsQuery.isLoading),
    railError: Boolean(sport === "mlb" && catalog.teamsEnabled && teamsQuery.isError),
  };
};
